import { logEvent } from 'firebase/analytics';
import { getAnalyticsSafe, setAnalyticsUserId } from './firebase';
import { enqueue } from './queue';
import { mirrorUxEventToHotjar } from './hotjarBridge';
import { getFlowVariantRaw } from '../utils/flowVariant';
import { getSignupUxVariantRaw } from '../utils/signupUxAb';
import { getSignupPriceNoticeArmRaw } from '../utils/signupPriceNoticeAb';
import { reconcilePatientIdStorage } from '../utils/patientIdStorage';

const MAX_VALUE_LENGTH = 120;

// Padrões que indicam PII ainda não sanitizado em params (defesa em profundidade).
// Se algum dia alguém logar acidentalmente CPF/email/telefone/cartão num evento,
// o valor é apagado antes de ir tanto pro GA4 quanto pro Firestore.
const CPF_REGEX = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/;
const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_REGEX = /\b(?:\+?55\s?)?(?:\(?\d{2}\)?\s?)?\d{4,5}-?\d{4}\b/;
const CC_REGEX = /\b(?:\d[ -]?){13,19}\b/;

// Chaves cujo nome sugere PII e devem ser totalmente apagadas.
// IMPORTANTE: `name` solo NÃO entra aqui — é uma chave de metadado legítima
// (ex.: web_vital usa { name: 'LCP' }). Para PII de pessoa, usamos `nome`,
// `fullname`, `patientname`, `patientName`. O filtro `looksLikePII` cuida de
// strings com formato de CPF/email/telefone/cartão como defesa em profundidade.
const PII_KEYS = /^(?:cpf|email|phone|telefone|fone|address|endereco|nome|fullname|patientname|fullName|patientName|password|senha|cardnumber|cardNumber|cvv|cvc|token|secret)$/i;

// IDs internos do app (patientId/doctorId/adminId) têm a forma
// `patient-<unix_ms>-<rand>` — ex.: `patient-1779059221052-q4c4dc`. Os 13 dígitos
// consecutivos do timestamp casam acidentalmente com CC_REGEX (auditoria 18/05
// caso Tarsila), fazendo todo `params.user_id` virar "[redacted]" e quebrando
// a busca de eventos por paciente no diagnóstico. A exceção abaixo preserva o ID
// quando a chave é claramente um identificador interno E o valor casa com o
// formato esperado — nunca preserva valor arbitrário só pelo nome da chave.
const INTERNAL_ID_KEYS = /^(?:user_id|patient_id|doctor_id|admin_id|patientId|doctorId|adminId)$/i;
const INTERNAL_ID_VALUE_RE = /^(?:patient|doctor|admin)-\d{10,}-[a-z0-9]{4,12}$/i;
const isInternalId = (key, text) =>
  !!key && INTERNAL_ID_KEYS.test(key) && typeof text === 'string' && INTERNAL_ID_VALUE_RE.test(text);

const looksLikePII = (text) => {
  if (CPF_REGEX.test(text)) return true;
  if (EMAIL_REGEX.test(text)) return true;
  if (PHONE_REGEX.test(text)) return true;
  if (CC_REGEX.test(text)) return true;
  return false;
};

// Chaves de URL: origin+pathname é seguro para logar (não vaza PII), mas
// query/hash frequentemente contém patientId, email, cpf etc. Antes de aplicar
// looksLikePII (que casaria com timestamps grandes ex. `1762345678123` na
// query e redactaria a URL inteira), removemos query/hash. Isso garante que
// fetch_error mantenha o endpoint visível para diagnóstico (ex. `/payments/status`).
const URL_KEYS = /^(?:url|href|src|location|location_full|api_url|requestUrl)$/i;
const sanitizeUrlValue = (text) => {
  try {
    // URL absoluta direta
    const u = new URL(text);
    return `${u.origin}${u.pathname}`;
  } catch {}
  try {
    // URL relativa: usa origin atual como base
    if (typeof window !== 'undefined' && window.location) {
      const u = new URL(text, window.location.origin);
      return `${u.origin}${u.pathname}`;
    }
  } catch {}
  return null; // Falha no parse: deixa o fluxo normal (looksLikePII) decidir
};

const sanitizeParams = (params) => {
  const sanitized = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (PII_KEYS.test(key)) {
      sanitized[key] = '[redacted]';
      return;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
      return;
    }
    if (typeof value === 'object') {
      // Objetos pequenos ainda podem ser úteis (ex.: device context).
      // Achatamos apenas chaves primitivas.
      const flat = {};
      Object.entries(value).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (PII_KEYS.test(k)) { flat[k] = '[redacted]'; return; }
        if (typeof v === 'number' || typeof v === 'boolean') { flat[k] = v; return; }
        let t = String(v);
        let urlSanitized = false;
        if (URL_KEYS.test(k)) {
          const stripped = sanitizeUrlValue(t);
          if (stripped) { t = stripped; urlSanitized = true; }
        }
        // Quando a URL ja foi sanitizada para origin+pathname, e seguro logar:
        // query/hash (que sao os vetores reais de PII) ja foram removidos. Sem
        // este short-circuit, IDs longos no pathname (timestamps, UUIDs) ainda
        // dispararam looksLikePII e a URL inteira virava "[redacted]" — o que
        // causava placeholder.local no aggregator (auditoria 13/05).
        if (!urlSanitized && !isInternalId(k, t) && looksLikePII(t)) { flat[k] = '[redacted]'; return; }
        flat[k] = t.length > MAX_VALUE_LENGTH ? t.slice(0, MAX_VALUE_LENGTH) : t;
      });
      sanitized[key] = flat;
      return;
    }
    let text = String(value);
    let urlSanitized = false;
    if (URL_KEYS.test(key)) {
      const stripped = sanitizeUrlValue(text);
      if (stripped) { text = stripped; urlSanitized = true; }
    }
    if (!urlSanitized && !isInternalId(key, text) && looksLikePII(text)) {
      sanitized[key] = '[redacted]';
      return;
    }
    sanitized[key] = text.length > MAX_VALUE_LENGTH ? text.slice(0, MAX_VALUE_LENGTH) : text;
  });
  return sanitized;
};

const getRoleFromStorage = () => {
  try {
    const role = localStorage.getItem('loginRole');
    if (role) return role;
  } catch {}
  return window.location.pathname.startsWith('/medico') ? 'doctor' : 'patient';
};

const getUserIdFromStorage = (role) => {
  try {
    if (role === 'doctor') return localStorage.getItem('doctorId') || 'doctor';
    return reconcilePatientIdStorage();
  } catch {
    return null;
  }
};

const getFlowVariantForTelemetry = () => {
  try {
    return getFlowVariantRaw() || null;
  } catch {
    return null;
  }
};

const getSignupUxVariantForTelemetry = () => {
  try {
    return getSignupUxVariantRaw() || null;
  } catch {
    return null;
  }
};

const getPriceNoticeArmForTelemetry = () => {
  try {
    return getSignupPriceNoticeArmRaw() || null;
  } catch {
    return null;
  }
};

const baseContext = () => {
  const role = getRoleFromStorage();
  return {
    role,
    user_id: getUserIdFromStorage(role) || null,
    // Marca todo evento com o braço do A/B de fluxo. Null = fora do
    // experimento, que na análise não pode ser confundido com o controle.
    flow_variant: role === 'patient' ? getFlowVariantForTelemetry() : null,
    // Braço do signup-ux-ab. O agregador de UX resolve o braço por sessão a
    // partir deste campo, então ele precisa estar em TODO evento — inclusive
    // nos de telas vistas antes do sorteio (a Home).
    signup_ux_variant: role === 'patient' ? getSignupUxVariantForTelemetry() : null,
    // Braço do signup-price-notice: segue a pessoa da tela do nome até o pagamento.
    price_notice_arm: role === 'patient' ? getPriceNoticeArmForTelemetry() : null,
    route: window.location.pathname,
    route_full: `${window.location.pathname}${window.location.search || ''}`,
    visibility: document.visibilityState,
    online: navigator.onLine,
    app_version: window.APP_VERSION || null,
    app_build_time: window.APP_BUILD_TIME || null,
  };
};

export const setUxUserFromStorage = async () => {
  const role = getRoleFromStorage();
  const userId = getUserIdFromStorage(role);
  if (userId) {
    await setAnalyticsUserId(userId);
  }
  return { role, userId };
};

/**
 * Constantes nomeadas para eventos UX da triagem — criadas após o smoke test
 * de 27/05/2026 que detectou (a) 60% de retrabalho quando paciente despeja
 * tudo na 1ª mensagem, (b) loop secundário quando paciente recusa o sexo,
 * (c) aceitação silenciosa de respostas vagas. Use estas constantes em vez
 * de string literal para evitar typos e facilitar criação de dashboards.
 */
export const UX_EVENTS = {
  /** Disparado quando um mesmo campo essencial é re-perguntado >1× na sessão. */
  TRIAGE_QUESTION_REPEATED: 'triage_question_repeated',
  /** Disparado quando o sistema aceita "prefiro não dizer" e segue adiante. */
  TRIAGE_REFUSAL_ACCEPTED: 'triage_refusal_accepted',
  /** Disparado quando detectamos marcador de correção retroativa do paciente. */
  TRIAGE_CORRECTION_DETECTED: 'triage_correction_detected',
  /** Disparado quando paciente responde algo vago ("?", "hmm", "talvez"). */
  TRIAGE_NULL_ANSWER: 'triage_null_answer',
  /** Disparado quando o paciente usa "Pular e ir direto para o chat". */
  TRIAGE_SKIP_USED: 'triage_skip_used',
  /** Disparado quando o cap rígido de turnos força finalização da triagem. */
  TRIAGE_FORCE_FINALIZE: 'triage_force_finalize',
  /** Finalização bloqueada (sem input clínico ou prontuário template vazio). */
  TRIAGE_FINALIZE_BLOCKED: 'triage_finalize_blocked',
  /** Modal “triagem não concluída” exibido ao retornar ao app. */
  TRIAGE_RESUME_MODAL_SHOWN: 'triage_resume_modal_shown',
  /** Overlay ProcessingScreen (finalizing/additional_q) excedeu o fail-safe. */
  TRIAGE_PROCESSING_PHASE_FAILSAFE: 'triage_processing_phase_failsafe',
  /** Disparado quando o prontuário final tem slot divergente do histórico. */
  PRONTUARIO_SLOT_MISMATCH: 'prontuario_slot_mismatch',
};

// Dual-emit: o mesmo evento vai para GA4 (agregação grátis) e para a fila
// first-party (Firestore via Cloud Function /ux/ingest, leitura no /ux-monitor).
// Nenhuma chamada existente de logUxEvent precisa mudar.
export const logUxEvent = async (eventName, params = {}) => {
  let payload;
  try {
    payload = sanitizeParams({ ...baseContext(), ...params });
  } catch {
    payload = {};
  }

  // 1) Fila first-party (síncrono, instantâneo).
  try {
    enqueue({ name: eventName, params: payload });
  } catch {}

  mirrorUxEventToHotjar(eventName, payload);

  // 2) GA4 (assíncrono, depende de inicialização e suporte do navegador).
  try {
    const analytics = await getAnalyticsSafe();
    if (analytics) logEvent(analytics, eventName, payload);
  } catch {}
};
